import { forwardRef, type ComponentPropsWithoutRef, type ReactNode } from 'react';
import * as RadixDialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { cn } from '../../lib/utils';
import { useTranslation } from 'react-i18next';

/**
 * The side sheet: a record detail pane that opens beside the list instead of on
 * top of it, so the row you came from stays visible. Same modal machinery as
 * `Dialog` (Radix, focus trap, Escape) and the same dark header (DS-43), but
 * anchored to the inline-end edge — which in Arabic is the left of the screen
 * (DS-50).
 */

export type DrawerWidth = 'sm' | 'md' | 'lg';

const WIDTH_CLASS: Record<DrawerWidth, string> = {
  sm: 'w-80',
  md: 'w-[26rem]',
  lg: 'w-[34rem]',
};

type ContentProps = ComponentPropsWithoutRef<typeof RadixDialog.Content>;

export interface DrawerProps extends Omit<ContentProps, 'title' | 'children'> {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  subtitle?: ReactNode;
  width?: DrawerWidth;
  children?: ReactNode;
  /** Action buttons; rendered in a fixed row at the bottom of the sheet. */
  footer?: ReactNode;
  closeOnOverlayClick?: boolean;
  /** Blocks Escape and the overlay while a form is dirty; the X still closes. */
  preventClose?: boolean;
  closeLabel?: string;
  bodyClassName?: string;
}

export const Drawer = forwardRef<HTMLDivElement, DrawerProps>(function Drawer(
  {
    open,
    onOpenChange,
    title,
    subtitle,
    width = 'md',
    children,
    footer,
    closeOnOverlayClick = true,
    preventClose = false,
    closeLabel: closeLabelProp,
    className,
    bodyClassName,
    onEscapeKeyDown,
    onInteractOutside,
    ...rest
  },
  ref,
) {
  const { t } = useTranslation('common');
  const closeLabel = closeLabelProp ?? t('actions.close');
  return (
    <RadixDialog.Root open={open} onOpenChange={onOpenChange}>
      <RadixDialog.Portal>
        <RadixDialog.Overlay className="fixed inset-0 z-40 bg-slate-900/60" />

        <RadixDialog.Content
          ref={ref}
          className={cn(
            // `end-0` + `border-s` keep the sheet on the inline-end edge with its
            // frame facing the content, in either direction.
            'fixed inset-y-0 end-0 z-50 flex max-w-[95vw] flex-col overflow-hidden',
            'animate-slideInEnd border-s border-slate-400 bg-white shadow-2xl',
            WIDTH_CLASS[width],
            // NFR-05 — the sheet exists to keep the row you came from visible
            // beside it. On a phone there is no beside: 320px of detail against
            // a 55px sliver of list is neither, so it takes the whole screen.
            'max-lg:w-full max-lg:max-w-none max-lg:border-s-0',
            className,
          )}
          onEscapeKeyDown={(event) => {
            if (preventClose) event.preventDefault();
            onEscapeKeyDown?.(event);
          }}
          onInteractOutside={(event) => {
            if (preventClose || !closeOnOverlayClick) event.preventDefault();
            onInteractOutside?.(event);
          }}
          {...(subtitle ? {} : { 'aria-describedby': undefined })}
          data-testid="drawer"
          {...rest}
        >
          <header
            className="flex shrink-0 items-start gap-3 bg-slate-800 px-4 py-2.5 text-white
                       max-lg:items-center max-lg:px-2 max-lg:pb-2
                       max-lg:pt-[calc(0.625rem+env(safe-area-inset-top,0px))]"
          >
            <div className="min-w-0 flex-1 max-lg:ps-1">
              <RadixDialog.Title data-testid="drawer-title" className="truncate text-sm font-bold leading-tight">
                {title}
              </RadixDialog.Title>
              {subtitle ? (
                <RadixDialog.Description className="mt-0.5 truncate text-[11px] text-slate-300">
                  {subtitle}
                </RadixDialog.Description>
              ) : null}
            </div>

            <RadixDialog.Close
              aria-label={closeLabel}
              data-testid="drawer-close"
              className="-me-1 shrink-0 rounded p-1 text-slate-300 transition hover:bg-slate-700 hover:text-white
                         max-lg:-me-0 max-lg:flex max-lg:h-touch max-lg:w-touch max-lg:items-center max-lg:justify-center"
            >
              <X className="h-4 w-4 max-lg:h-5 max-lg:w-5" aria-hidden />
            </RadixDialog.Close>
          </header>

          <div
            className={cn(
              'touch-scroll flex-1 overflow-y-auto overscroll-contain p-4 max-lg:p-3',
              bodyClassName,
            )}
          >
            {children}
          </div>

          {footer ? (
            <div
              className="flex shrink-0 items-center justify-end gap-2 border-t border-slate-200 bg-slate-50 px-4 py-2.5
                         max-lg:px-3 max-lg:pt-2 max-lg:pb-[calc(0.625rem+env(safe-area-inset-bottom,0px))]
                         max-lg:[&>button]:flex-1"
            >
              {footer}
            </div>
          ) : null}
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
});
